import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Coins, Clock } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { rewards, type CoinsResponse } from '../api/client';

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Range label for the "Exp in" chip, e.g. "1–30 Nov" — the calendar month the
 * expiring coins fall in. Each lot expires on its own date somewhere in that
 * window (12 months after it was earned), so we show the whole month span
 * rather than a single day. Derived from the month-end ISO the API returns;
 * falls back to the current calendar month before data loads.
 */
export function expiryChipLabel(on?: string | null): string {
  let month: number;
  let lastDay: number;
  if (on) {
    const [, m, d] = on.split('-').map(Number);
    month = m - 1;
    lastDay = d;
  } else {
    const now = new Date();
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    month = last.getMonth();
    lastDay = last.getDate();
  }
  return `1–${lastDay} ${SHORT_MONTHS[month]}`;
}

/**
 * Compact ZS Coins summary — Available + Expiring (this month), with a
 * "VIEW DETAILS" link to the full My ZS Coins page. Shown on the Profile page.
 */
export default function CoinSummaryCard() {
  const { isDark } = useTheme();
  const [data, setData] = useState<CoinsResponse | null>(null);

  useEffect(() => {
    let active = true;
    rewards.coins().then((r) => { if (active) setData(r); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const card = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const sub = isDark ? 'text-gray-400' : 'text-gray-500';
  const available = data?.balance ?? 0;
  const expiring = data?.expiring_this_month?.amount ?? 0;
  // Permanent chip above the clock icon: the current month's expiry deadline.
  const expOn = data?.expiring_this_month?.on;

  return (
    <div className={`rounded-2xl border p-5 ${card}`}>
      <div className="grid grid-cols-2 gap-4">
        {/* Available */}
        <div className="flex flex-col items-center text-center">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mb-2 shadow-sm">
            <Coins size={20} className="text-white" />
          </div>
          <p className="text-2xl font-extrabold">{available.toLocaleString('en-IN')}</p>
          <p className={`text-xs ${sub}`}>Available</p>
        </div>

        {/* Expiring this month */}
        <div className="flex flex-col items-center text-center">
          <div className="relative w-11 h-11 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center mb-2 shadow-sm">
            <Clock size={20} className="text-white" />
            <span className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-bold px-3 py-1 rounded-xl bg-emerald-600 text-white shadow-md">
              Exp in {expiryChipLabel(expOn)}
            </span>
          </div>
          <p className="text-2xl font-extrabold">{expiring.toLocaleString('en-IN')}</p>
          <p className={`text-xs ${sub}`}>Expiring</p>
        </div>
      </div>

      {/* Lifetime savings */}
      <div className="mt-4 flex items-center justify-center gap-3 rounded-xl px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-sm">
        <Coins size={16} />
        <span className="text-sm font-semibold">Total ZS Coins savings</span>
        <span className="inline-flex items-center gap-1 text-base font-extrabold text-amber-300"><span className="leading-none" aria-hidden="true">🪙</span>{(data?.total_earned ?? 0).toLocaleString('en-IN')}</span>
      </div>

      <Link
        to="/rewards/coins"
        className={`mt-3 block text-center text-sm font-bold tracking-wide py-2.5 rounded-xl transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-emerald-400' : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700'}`}
      >
        VIEW DETAILS
      </Link>
    </div>
  );
}
